import {
  IUseInfiniteScrollOptions,
  useInfiniteScroll,
} from 'components/admin/ui';
import { buildUnauthorizedSoftLoginRedirectPath } from 'lib/auth/helpers';
import { FetchKey, ISwrConfiguration, ISwrResponse, useFetch } from 'lib/hooks';
import { ISafeFetchConfigOptions, safeFetch } from 'lib/requests/fetch';
import { UnauthorizedErrorType } from 'lib/requests/types';
import { EthenaQueryParam } from 'lib/types';
import { NextPageContext } from 'next';

const learningCenterFetchConfigOptions: ISafeFetchConfigOptions = {
  buildUnauthorizedRedirectPath: buildUnauthorizedSoftLoginRedirectPath,
  serverHeaders: (ctx: NextPageContext) => ({
    'X-Scorm-Key': (ctx.query.scormKey as string) || '',
  }),
  clientHeaders: () => {
    const urlParams = new URLSearchParams(window.location.search);
    const scormKey = urlParams.get(EthenaQueryParam.SCORM_USER_LOGIN_KEY) || '';
    return scormKey
      ? {
          'X-Scorm-Key':
            urlParams.get(EthenaQueryParam.SCORM_USER_LOGIN_KEY) || '',
        }
      : {};
  },
};

export const learningCenterSafeFetch = async <T = any>(
  url: string,
  fetchOptions?: RequestInit,
  configOptions?: ISafeFetchConfigOptions
) =>
  safeFetch<T>(url, fetchOptions, {
    ...configOptions,
    ...learningCenterFetchConfigOptions,
    serverHeaders: (ctx: NextPageContext) => ({
      ...configOptions?.serverHeaders?.(ctx),
      ...learningCenterFetchConfigOptions.serverHeaders(ctx),
    }),
    clientHeaders: () => ({
      ...configOptions?.clientHeaders?.(),
      ...learningCenterFetchConfigOptions.clientHeaders(),
    }),
  });

export const learningCenterAllowNotAuthedSafeFetch = async <T = any>(
  url: string,
  fetchOptions?: any,
  configOptions?: ISafeFetchConfigOptions
) =>
  safeFetch<T>(url, fetchOptions, {
    ...configOptions,
    ...learningCenterFetchConfigOptions,
    serverHeaders: (ctx: NextPageContext) => ({
      ...configOptions?.serverHeaders?.(ctx),
      ...learningCenterFetchConfigOptions.serverHeaders(ctx),
    }),
    clientHeaders: () => ({
      ...configOptions?.clientHeaders?.(),
      ...learningCenterFetchConfigOptions.clientHeaders(),
    }),
    dontRedirectForErrorTypes: [UnauthorizedErrorType.NOT_AUTHED],
  });

export const useLearningCenterFetch = <T>(
  key: FetchKey,
  options: ISwrConfiguration = {}
): ISwrResponse<T> =>
  useFetch<T>(key, options, learningCenterFetchConfigOptions);

export const useLearningCenterInfiniteScroll = <T>(
  endpoint: string,
  pageSize: number,
  options: IUseInfiniteScrollOptions = {}
) =>
  useInfiniteScroll<T>(
    endpoint,
    pageSize,
    options,
    learningCenterFetchConfigOptions
  );
